// src/pages/ia-paca/metiers/ai-automation-specialist.tsx
//
// Page métier — AI Automation Specialist en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Workflow, Brain, Sparkles, Database, ShieldCheck, Cpu } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'ai-automation-specialist',
  metaTitle: 'Recrutement AI Automation Specialist en PACA — n8n, Make, Zapier + LLM | recruteurs.io',
  metaDescription:
    "Recruter un AI Automation Specialist en PACA : profils n8n, Make, Zapier + LLM, automatisation de workflows métier avec IA. Salaire 48-85k€. Profils en forte croissance.",
  metaKeywords:
    'recrutement AI Automation Specialist, n8n PACA, Make Marseille, Zapier IA, workflow automation Sophia, salaire AI Automation 2026, recruter automatiseur IA',
  heroLabel: 'AI Automation Specialist',
  heroSubtitle:
    "Le métier qui transforme votre back-office en machine. Un profil qui combine maîtrise des outils no-code (n8n, Make, Zapier) + appels LLM intelligents pour automatiser des process métier complets. Le ROI le plus visible côté ETI / PME.",
  heroBadges: ['n8n', 'Make', 'Zapier', 'LLM workflows', 'Process metier'],
  jsonLdName: 'AI Automation Specialist',
  jsonLdDescription:
    "L'AI Automation Specialist conçoit et opère des workflows métier automatisés enrichis par LLM : n8n, Make, Zapier + APIs IA. Profile mixte tech + analyste process.",
  jsonLdSkills:
    'n8n (self-hosted + cloud), Make (Integromat), Zapier, Airtable, Webhook, REST APIs, LLM APIs (OpenAI, Mistral, Anthropic), JavaScript basique, SQL basique, BPMN, process mining',
  jsonLdMinSalary: 48000,
  jsonLdMedianSalary: 62000,
  jsonLdMaxSalary: 85000,
  jsonLdEducation: 'Profil souvent autodidacte ou reconverti (ops, dev junior, analyste).',
  jsonLdExperience: 'Junior (1-2 ans) à Senior (5+ ans).',
  intro: {
    title: 'AI Automation Specialist : le ROI le plus tangible côté ETI/PME en 2026.',
    paragraphs: [
      "Pendant que tout le monde parle d'agents autonomes complexes, une autre vague — moins glamour mais ultra-rentable — déferle : <strong>l'automatisation de workflows métier enrichis par LLM</strong>. Tri d'emails, qualification de leads, extraction de données depuis des PDF, génération de devis, dispatching tickets support. L'AI Automation Specialist orchestre tout ça.",
      "Concrètement : il combine des outils no-code (n8n self-hosted, Make, Zapier) avec des appels LLM ciblés (classification, extraction structurée via function calling, génération de réponses) et des bases de données simples (Airtable, Postgres). Résultat : 5-30 heures hebdo économisées par équipe métier, ROI visible en 30 jours.",
      "<strong>Pourquoi ce métier explose :</strong> les ETI et PME PACA n'ont pas le budget pour un AI Engineer à 80 k€. Mais elles peuvent recruter un AI Automation Specialist à 55 k€ qui leur livre 80% de la valeur sur les cas d'usage process. Marché en croissance +40% sur 12 mois.",
    ],
  },
  stackTitle: 'Les outils que votre AI Automation Specialist doit maîtriser.',
  stack: [
    {
      Icon: Workflow,
      title: 'Plateformes no-code',
      tools: ['n8n (self-hosted + cloud)', 'Make (Integromat)', 'Zapier', 'Activepieces', 'Pipedream'],
    },
    {
      Icon: Brain,
      title: 'Intégrations LLM',
      tools: ['OpenAI API', 'Anthropic API', 'Mistral API', 'function calling', 'structured output'],
    },
    {
      Icon: Database,
      title: 'Stockage & data',
      tools: ['Airtable', 'Notion API', 'Google Sheets', 'Postgres / Supabase', 'NocoDB'],
    },
    {
      Icon: Sparkles,
      title: 'Connecteurs & APIs',
      tools: ['Gmail / Outlook', 'Slack / Teams', 'HubSpot / Pipedrive', 'Stripe', 'GitHub / GitLab', 'Webhook + REST'],
    },
    {
      Icon: Cpu,
      title: 'Dev appoint',
      tools: ['JavaScript basique', 'SQL basique', 'cURL', 'jq', 'Postman', 'Docker compose (n8n self-host)'],
    },
    {
      Icon: ShieldCheck,
      title: 'Sécurité & ops',
      tools: ['Gestion secrets', 'OAuth', 'Rate limiting', 'Error handling', 'Monitoring (logs n8n, alertes Slack)'],
    },
  ],
  salaryByCityTitle: '48-85 k€, ROI client visible rapidement = packages qui montent.',
  salaryByCity: [
    { ville: 'Marseille', junior: '48 – 55 k€', confirme: '55 – 70 k€', senior: '70 – 88 k€' },
    { ville: 'Aix-en-Provence', junior: '46 – 53 k€', confirme: '53 – 68 k€', senior: '68 – 85 k€' },
    { ville: 'Sophia Antipolis', junior: '50 – 58 k€', confirme: '58 – 75 k€', senior: '75 – 92 k€' },
    { ville: 'Nice', junior: '48 – 55 k€', confirme: '55 – 72 k€', senior: '72 – 88 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un AI Automation Specialist.',
  questionsIntro:
    "Ces questions séparent un AI Automation Specialist qui shippe en prod d'un utilisateur Zapier qui a fait 3 templates.",
  questions: [
    {
      q: "Décris un workflow d'automatisation que tu as livré en prod. ROI mesurable ?",
      good:
        "Cite un cas concret : process initial (X heures/semaine, Y erreurs), workflow conçu (étapes, outils, LLM calls), résultats (heures économisées, taux d'erreur, satisfaction utilisateurs). Quantifie le ROI. Sait aussi raconter les pièges (cas limites, gestion d'erreur, ré-itérations).",
      bad: "'J'ai fait des templates Zapier.' Pas de cas d'usage métier, pas de ROI mesuré.",
    },
    {
      q: 'n8n self-hosted vs Make vs Zapier — comment tu choisis pour un client ?',
      good:
        "Critères : volume d'exécutions (Zapier devient très cher au-delà de 50k tasks/mois, Make plus rentable), souveraineté des données (n8n self-hosted = données on-prem), complexité workflows (n8n + JS pour cas complexes), équipe cliente (no-code pur → Make, dev appoint → n8n). Connaît les coûts.",
      bad: "'Zapier c'est le meilleur.' Réponse dogmatique. Ignore les coûts à l'échelle et les contraintes data.",
    },
    {
      q: "Tu dois extraire 5 champs structurés depuis une facture PDF. Quelle approche ?",
      good:
        "Pipeline : 1) extraction texte (Unstructured, Apryse, Mistral OCR, GPT-4o vision pour cas complexes), 2) appel LLM avec function calling / structured output (Pydantic + JSON schema), 3) validation (regex, ranges), 4) fallback humain si confidence faible, 5) injection dans Airtable ou ERP. Sait gérer les variantes de format.",
      bad: "'Je fais un prompt qui demande JSON.' Pas de notion structured output, validation absente, erreurs fréquentes.",
    },
    {
      q: 'Comment tu gères les erreurs et retry dans un workflow n8n qui appelle OpenAI ?',
      good:
        "Patterns : retry avec backoff exponentiel sur rate limit, fallback sur modèle de secours (Claude si OpenAI down), dead letter queue pour les erreurs persistantes, alerte Slack au-delà d'un seuil, monitoring du taux d'erreur. Connaît les status codes (429, 500, timeout).",
      bad: "'Si ça plante, ça plante.' Aucune gestion d'erreur, workflow fragile.",
    },
    {
      q: "Comment tu sécurises un workflow qui traite des données clients (RGPD) ?",
      good:
        "Mesures : self-hosted n8n si possible, OpenAI Enterprise / Mistral pour data-sensitive (zero data retention), scrubbing PII en amont si pas critique pour le LLM, secrets dans vault (pas dans le workflow), logs sans données sensibles, accès workflow restreint (RBAC), audit trail.",
      bad: "'On envoie tout à OpenAI standard.' Risque RGPD majeur, non-conformité.",
    },
  ],
  ctaTitle: "Vous cherchez un AI Automation Specialist en PACA ?",
  ctaSubtitle:
    "Profil rentable et accessible. Notre court-list cible 5-7 candidats avec ROI client prouvé en 15-25 jours.",
};

export default function AiAutomationSpecialistPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
